import { useMemo, useState } from 'react'
import { CategoryIcon, colorVar } from './CategoryIcon'
import { CategoryEditor, type CategoryDraft } from './CategoryEditor'
import { ApiError, api, type CategoryDef } from '../lib/api'
import { useStore } from '../lib/store'
import './CategorySettings.css'

function describe(e: unknown): string {
  if (e instanceof ApiError && e.isDuplicateCategory) {
    return 'You already have a category with that name.'
  }
  return e instanceof Error ? e.message : String(e)
}

export function CategorySettings() {
  const {
    categories,
    reminders,
    createCategory,
    updateCategory,
    deleteCategory,
    refreshCategories,
  } = useStore()
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Deleting a category never touches reminders — they just fall back to
  // Uncategorized — so the count is what makes that an informed choice.
  const counts = useMemo(() => {
    const tally = new Map<string, number>()
    for (const r of reminders) tally.set(r.category, (tally.get(r.category) ?? 0) + 1)
    return tally
  }, [reminders])

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key)
    setError(null)
    try {
      await fn()
      return true
    } catch (e) {
      setError(describe(e))
      return false
    } finally {
      setBusy(null)
    }
  }

  async function create(draft: CategoryDraft) {
    if (await run('new', () => createCategory(draft))) setCreating(false)
  }

  async function save(cat: CategoryDef, draft: CategoryDraft) {
    if (await run(cat.id, () => updateCategory(cat.id, draft))) setEditing(null)
  }

  async function remove(cat: CategoryDef) {
    await run(cat.id, () => deleteCategory(cat.id))
    setConfirming(null)
  }

  /** Reordering rewrites sort_order for anything whose position changed. The
   * seeded rows can all share a sort_order, so swapping a pair is not enough —
   * the first move normalises the whole list, later ones patch two rows. */
  async function move(index: number, delta: number) {
    const next = [...categories]
    const target = index + delta
    const moved = next[index]
    const other = next[target]
    if (!moved || !other) return
    next[index] = other
    next[target] = moved

    // Straight to the API, not through the store: the store mutator re-pulls
    // categories *and* reminders per call, and a normalising pass can be a dozen
    // of them. One refresh at the end is enough — sort_order touches nothing else.
    await run(moved.id, async () => {
      for (const [i, cat] of next.entries()) {
        if (cat.sort_order !== i) await api.updateCategory(cat.id, { sort_order: i })
      }
      await refreshCategories()
    })
  }

  return (
    <>
      <h2 className="section-label">
        Categories
        <span className="section-label__value">{categories.length}</span>
      </h2>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}

      <div className="stack">
        {categories.map((cat, i) => {
          const count = counts.get(cat.slug) ?? 0
          const color = colorVar(cat.color)
          const isBusy = busy === cat.id

          if (editing === cat.id) {
            return (
              <CategoryEditor
                key={cat.id}
                initial={{ label: cat.label, color: cat.color, icon: cat.icon }}
                submitLabel="Save"
                busy={isBusy}
                onSubmit={(draft) => save(cat, draft)}
                onCancel={() => {
                  setEditing(null)
                  setError(null)
                }}
              />
            )
          }

          return (
            <div
              key={cat.id}
              className={`catrow glass${isBusy ? ' catrow--busy' : ''}`}
              style={{ ['--cat' as string]: color }}
            >
              <span className="catrow__tile" aria-hidden="true">
                <CategoryIcon icon={cat.icon} color={color} size={22} />
              </span>

              <div className="catrow__body">
                <strong>{cat.label}</strong>
                <span>
                  {count} {count === 1 ? 'reminder' : 'reminders'}
                </span>
              </div>

              <div className="catrow__order">
                <button
                  className="catrow__arrow"
                  disabled={i === 0 || isBusy}
                  aria-label={`Move ${cat.label} up`}
                  onClick={() => move(i, -1)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  className="catrow__arrow"
                  disabled={i === categories.length - 1 || isBusy}
                  aria-label={`Move ${cat.label} down`}
                  onClick={() => move(i, 1)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 9l7 7 7-7" />
                  </svg>
                </button>
              </div>

              <div className="catrow__actions">
                <button
                  className="catrow__act"
                  disabled={isBusy}
                  onClick={() => {
                    setError(null)
                    setConfirming(null)
                    setEditing(cat.id)
                  }}
                >
                  Edit
                </button>
                {/* Two taps, inline — window.confirm blocks the page and breaks
                    the glass look, same as on the Done screen. */}
                <button
                  className={`catrow__act catrow__act--danger${
                    confirming === cat.id ? ' is-confirming' : ''
                  }`}
                  disabled={isBusy}
                  onClick={() => (confirming === cat.id ? remove(cat) : setConfirming(cat.id))}
                  onBlur={() => setConfirming((c) => (c === cat.id ? null : c))}
                  aria-label={
                    confirming === cat.id
                      ? `Confirm delete ${cat.label}`
                      : `Delete ${cat.label}`
                  }
                >
                  {confirming === cat.id ? 'Sure?' : 'Delete'}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {creating ? (
        <CategoryEditor
          submitLabel="Create"
          busy={busy === 'new'}
          autoFocus
          onSubmit={create}
          onCancel={() => {
            setCreating(false)
            setError(null)
          }}
        />
      ) : (
        <button
          className="btn btn--ghost catrow__add"
          onClick={() => {
            setError(null)
            setCreating(true)
          }}
        >
          Add category
        </button>
      )}

      <p className="hint">
        Deleting a category never deletes reminders — they keep their name and show as
        Uncategorized until you move them.
      </p>
    </>
  )
}
